import { Link } from 'react-router-dom';
import { products } from '@/mocks/products';
import { cdn } from '@/config/cdn';
import PhotoMotion from '@/components/feature/PhotoMotion';
import ProductCaption, { fmtPrice } from './ProductCaption';

type Product = (typeof products)[number];
type Size = 'lg' | 'md' | 'sm';

type Motion = { delay?: number; fromScale?: number; fromY?: number };

function Tile({ p, index, ratio, size = 'md', focus = 'object-center', motion }: { p: Product; index: number; ratio: string; size?: Size; focus?: string; motion?: Motion }) {
  const img = <img src={cdn(p.coverImage)} alt={p.name} title={`${p.name} — FOLD. ${p.category.toLowerCase()}`} className={`fold-img-hover w-full h-full object-cover ${focus}`} />;
  return (
    <Link to={`/product/${p.slug}`} className="group block cursor-pointer focus-visible:outline-none" aria-label={`${p.name}, ${fmtPrice(p.price)} — view details`}>
      <div className={`w-full ${ratio} overflow-hidden bg-[#e4e0da]`}>
        {motion ? <PhotoMotion {...motion}>{img}</PhotoMotion> : img}
      </div>
      <ProductCaption p={p} index={index} size={size} />
    </Link>
  );
}

/* A — featured */
function Feature({ p, index }: { p: Product; index: number }) {
  return (
    <article>
      <Link to={`/product/${p.slug}`} className="group block cursor-pointer" aria-label={`${p.name}, ${fmtPrice(p.price)} — view details`}>
        <div className="w-full aspect-[4/5] sm:aspect-[16/10] lg:aspect-[21/10] overflow-hidden bg-[#e4e0da]">
          <PhotoMotion fromScale={1.06} fromY={26} duration={1.25} parallax={22}>
            <img src={cdn(p.coverImage)} alt={p.name} title={`${p.name} — FOLD. ${p.category.toLowerCase()}`} className="fold-img-hover w-full h-full object-cover object-[50%_55%]" />
          </PhotoMotion>
        </div>
        <div className="w-full min-w-0">
          <ProductCaption p={p} index={index} size="lg" />
        </div>
      </Link>
    </article>
  );
}

/* B — asymmetric pair; alternates 7:5 and 5:7 with different image ratios */
function Pair({ items, start, flip, pilot = false }: { items: Product[]; start: number; flip: boolean; pilot?: boolean }) {
  const [a, b] = items;
  if (!b) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-12 gap-x-6">
        <article className="md:col-span-8 md:col-start-3 min-w-0"><Tile p={a} index={start} ratio="aspect-[4/3]" motion={{ fromScale: 1.05, fromY: 22 }} /></article>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-x-6 gap-y-14 md:items-end">
      <article className={`min-w-0 ${flip ? 'md:col-span-5 md:order-2 md:col-start-8' : 'md:col-span-7'}`}>
        <Tile p={a} index={start} ratio={flip ? 'aspect-[3/4]' : 'aspect-[6/5]'} motion={pilot ? { fromScale: 1.05, fromY: 22 } : { delay: flip ? 0.12 : 0, fromScale: 1.05, fromY: 22 }} />
      </article>
      <article className={`min-w-0 ${flip ? 'md:col-span-6 md:order-1 md:col-start-1' : 'md:col-span-4 md:col-start-9'}`}>
        <Tile p={b} index={start + 1} ratio={flip ? 'aspect-[1/1]' : 'aspect-[4/5]'} size={flip ? 'md' : 'sm'} motion={pilot ? { delay: 0.14, fromScale: 1.045, fromY: 18 } : { delay: flip ? 0 : 0.16, fromScale: 1.045, fromY: 18 }} />
      </article>
    </div>
  );
}

/* C — curated closing: one prominent + stacked supporting pieces */
function Closing({ items, start }: { items: Product[]; start: number }) {
  const [lead, ...support] = items;
  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-x-6 gap-y-14 border-t border-[rgba(23,21,21,0.14)] pt-10 md:pt-14">
      <article className="md:col-span-7 min-w-0">
        <Tile p={lead} index={start} ratio="aspect-[4/5]" motion={{ fromScale: 1.055, fromY: 24 }} />
      </article>
      <div className="md:col-span-4 md:col-start-9 min-w-0 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-1 gap-x-6 gap-y-12 content-between">
        {support.map((p, i) => (
          <article key={p.id} className="min-w-0">
            <Tile p={p} index={start + 1 + i} ratio="aspect-[5/4]" size="sm" motion={{ delay: 0.12 + i * 0.06, fromScale: 1.04, fromY: 16 }} />
          </article>
        ))}
      </div>
    </div>
  );
}

export default function CollectionGrid({ items }: { items: Product[] }) {
  if (items.length === 0) {
    return <p className="fold-shell py-24 text-sm text-[#5d5853]">Nothing in this category today.</p>;
  }
  const [first, ...rest] = items;
  const pairs: Product[][] = [];
  let remaining = rest;
  while (remaining.length > 3) {
    pairs.push(remaining.slice(0, 2));
    remaining = remaining.slice(2);
  }
  // 3 left → curated closing; 1–2 left → a final pair (or centred single)
  const closing = remaining.length === 3 ? remaining : [];
  if (remaining.length > 0 && remaining.length < 3) pairs.push(remaining);
  const closingStart = 2 + pairs.reduce((s, g) => s + g.length, 0);

  return (
    <section className="fold-shell pt-12 md:pt-16 pb-24 md:pb-40 overflow-x-clip" aria-label="Products">
      <Feature p={first} index={1} />
      <div className="mt-20 md:mt-32 flex flex-col gap-20 md:gap-32">
        {pairs.map((g, i) => {
          const start = 2 + pairs.slice(0, i).reduce((s, x) => s + x.length, 0);
          return <Pair key={g[0].id} items={g} start={start} flip={i % 2 === 1} pilot={i === 0} />;
        })}
        {closing.length > 0 && <Closing items={closing} start={closingStart} />}
      </div>
    </section>
  );
}